import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { sendEvent } from '../../utilities/analytics';
import ImportColumnLayout from '../layout/ImportColumnLayout';
import { classNames } from '../../utilities/styleUtils';
import {
  RocketLaunchIcon,
  BeakerIcon,
  HashtagIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Button from '../controls/Button';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { Input } from '../controls/Input';
import axios from 'axios';
import { getConfig } from '../../config/config-helper';
import { InputSelect, type SelectOption } from '../controls/InputSelect';
import {
  fetchSnowflakeAccounts,
  selectAccounts,
  confirmSnowflakeConnection,
} from '../../reducers/snowflakeAccountsSlice';
import { useSolution } from '../../hooks/useSolution';
import { fetchSolution } from '../../reducers/solutionsSlice';
import InformationTooltip from '../common/InformationTooltip';
import { AddAccountDialog } from '../snowflake-accounts/AddAccountDialog';
import { salesForceIdValidation, ValidationLevel } from '../../utilities/validation';
import { getSolutionTitle, isCustomerNameValid, isSolutionPrefixValid } from '../../utilities/solutionUtils';
import { selectSolutionDeployments } from '../../reducers/solutionDeploymentsSlice';
import type { ISolutionDeploymentErrorResponse } from '../../interfaces/ISolutionDeployment';

interface SolutionNewSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function SolutionNewSection({ title, description, children }: SolutionNewSectionProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-dataops-primary-dark-blue">{title}</h2>
        {description && <p className="mt-1 text-sm leading-6 text-gray-600 whitespace-pre-line">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

interface CreateSolutionDeploymentRequest {
  snowflake_account_id: number;
  project_id: number;
  git_ref: string;
  pipeline_filename: string;
  solution_prefix: string;
  extra_env_vars: Record<string, string>;
}

export default function NewSolutionDeployment() {
  const { dataopsAdminAppApiEndpoint } = getConfig();
  const { canConsume, canPublish } = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useToken();
  const [searchParams] = useSearchParams();

  // Form state
  const [solutionPrefix, setSolutionPrefix] = useState('');
  const [prefixError, setPrefixError] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<SelectOption | null>(null);
  const [accountConnectionStatus, setAccountConnectionStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>(
    'idle',
  );
  const [accountConnectionError, setAccountConnectionError] = useState('');
  const [gitRef, setGitRef] = useState('main');
  const [pipelineFilename, setPipelineFilename] = useState('full-ci.yml');
  const [customerName, setCustomerName] = useState('');
  const [customerNameError, setCustomerNameError] = useState('');
  const [useCaseId, setUseCaseId] = useState('');
  const [useCaseIdError, setUseCaseIdError] = useState('');
  const [customerLogoUrl, setCustomerLogoUrl] = useState('');
  const [customerLogoUrlError, setCustomerLogoUrlError] = useState<boolean>(true);
  const [extraEnvVars, setExtraEnvVars] = useState<Record<string, string>>({});
  const [envVarKey, setEnvVarKey] = useState('');
  const [envVarValue, setEnvVarValue] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [isAddAccountDialogOpen, setIsAddAccountDialogOpen] = useState(false);
  const [isAdvancedOptionsExpanded, setIsAdvancedOptionsExpanded] = useState(false);

  // Get existing solution deployments to find duplicates
  const existingDeployments = useAppSelector(selectSolutionDeployments);

  // Get solution from URL parameter
  const solutionId = searchParams.get('solution');
  const { solution } = useSolution(solutionId ?? undefined);

  // Get Snowflake accounts
  const snowflakeAccounts = useAppSelector(selectAccounts);
  const accountOptions: SelectOption[] = snowflakeAccounts.map((account) => ({
    id: account.id.toString(),
    name: `${account.account} (${account.username})`,
    description: account.warehouse ? `Warehouse: ${account.warehouse}` : undefined,
  }));

  // Fetch solution details
  useEffect(() => {
    if (solutionId && token) {
      const fullPath = `/solutions/${solutionId}`;
      dispatch(fetchSolution({ token, fullPath, iid: solutionId })).catch((err) => {
        console.error('Failed to fetch solution:', err);
      });
    }
  }, [solutionId, token, dispatch]);

  // Fire checkout-start event once the solution is loaded
  useEffect(() => {
    if (solution) {
      sendEvent({
        name: 'deployment-checkout-start',
        properties: { listingName: solution.listing?.name, solutionProjectId: solution.id, workflow: 'new' },
      });
    }
  }, [solution?.id]); // fire once when solution is first loaded

  // Fetch Snowflake accounts
  useEffect(() => {
    if (token) {
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to fetch Snowflake accounts:', err);
      });
    }
  }, [token, dispatch]);

  const checkAccountConnection = useCallback(async () => {
    if (!selectedAccount || !token) return;

    setAccountConnectionStatus('checking');
    setAccountConnectionError('');

    try {
      await dispatch(confirmSnowflakeConnection({ token, accountId: selectedAccount.id })).unwrap();
      setAccountConnectionStatus('valid');
    } catch (err: any) {
      setAccountConnectionStatus('invalid');
      setAccountConnectionError(err.message || 'Failed to connect to Snowflake account');
    }
  }, [selectedAccount, token, dispatch]);

  // Auto-check connection when account is selected
  useEffect(() => {
    if (selectedAccount) {
      void checkAccountConnection();
    } else {
      setAccountConnectionStatus('idle');
      setAccountConnectionError('');
    }
  }, [selectedAccount, checkAccountConnection]);

  // Validate solution prefix on change and when selected account changes
  useEffect(() => {
    if (solutionPrefix) {
      validatePrefix(solutionPrefix);
    }
  }, [selectedAccount, solutionPrefix]);

  const solutionDeploymentExists = (prefix: string): boolean => {
    // Check if a solution deployment already exists with the same prefix, snowflake account, and project
    if (!selectedAccount || !solution) {
      return false;
    }

    return existingDeployments.some(
      (d) =>
        d.solution_prefix === prefix &&
        d.snowflake_account_id === parseInt(selectedAccount.id) &&
        d.project_id === parseInt(solution.id),
    );
  };

  const validatePrefix = (prefix: string): boolean => {
    if (!prefix) {
      setPrefixError('Solution prefix is required');
      return false;
    }

    if (!isSolutionPrefixValid(prefix)) {
      setPrefixError(
        'The solution prefix can only contain letters, digits, and non-sequential underscores/dashes. It must start with a letter or a digit.',
      );
      return false;
    }

    if (solutionDeploymentExists(prefix)) {
      setPrefixError('Solution prefix already exists for this Snowflake account and solution. Try another.');
      return false;
    }

    if (prefix.length > 50) {
      setPrefixError('Prefix must be 50 characters or less');
      return false;
    }
    setPrefixError('');
    return true;
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setSolutionPrefix(value);
    validatePrefix(value);
  };

  const handleAddEnvVar = () => {
    if (envVarKey && envVarValue) {
      setExtraEnvVars({ ...extraEnvVars, [envVarKey]: envVarValue });
      setEnvVarKey('');
      setEnvVarValue('');
    }
  };

  const handleRemoveEnvVar = (key: string) => {
    const { [key]: _, ...newVars } = extraEnvVars;
    setExtraEnvVars(newVars);
  };

  const handleAddAccountSuccess = (accountId: number) => {
    setIsAddAccountDialogOpen(false);
    // Refresh the accounts list and auto-select the newly created account
    if (token) {
      dispatch(fetchSnowflakeAccounts(token))
        .unwrap()
        .then((accounts) => {
          // Find the newly created account by ID
          const newAccount = accounts.find((acc) => acc.id === accountId);
          if (newAccount) {
            const newAccountOption: SelectOption = {
              id: newAccount.id.toString(),
              name: `${newAccount.account} (${newAccount.username})`,
              description: newAccount.warehouse ? `Warehouse: ${newAccount.warehouse}` : undefined,
            };
            setSelectedAccount(newAccountOption);
          }
        })
        .catch((err) => {
          console.error('Failed to refresh Snowflake accounts:', err);
        });
    }
  };

  const handleManageAccountsClick = () => {
    window.open('/manage/accounts', '_blank');
  };

  const handleSubmit = async () => {
    setError(undefined);
    setIsSubmitting(true);

    // Validate required fields
    if (!solution) {
      setError('Solution not found');
      setIsSubmitting(false);
      return;
    }

    // Validate customer name if provided
    if (customerName && !isCustomerNameValid(customerName)) {
      setError(
        "The customer name can only contain letters, digits, '_', '.', dash, space. It must start with a letter or a digit.",
      );
      setIsSubmitting(false);
      return;
    }

    if (!validatePrefix(solutionPrefix)) {
      setIsSubmitting(false);
      return;
    }

    // Validate use case ID if provided
    if (useCaseIdError !== '') {
      setError('Use Case ID is invalid: ' + useCaseIdError);
      setIsSubmitting(false);
      return;
    }

    if (!selectedAccount) {
      setError('Please select a Snowflake account');
      setIsSubmitting(false);
      return;
    }

    if (accountConnectionStatus !== 'valid') {
      setError('Please ensure the Snowflake account connection is valid');
      setIsSubmitting(false);
      return;
    }

    try {
      // Build extra_env_vars with specific fields and custom vars
      const allEnvVars: Record<string, string> = { ...extraEnvVars };

      if (customerName) {
        allEnvVars.DATAOPS_CATALOG_CUSTOMER_NAME = customerName;
      }
      if (useCaseId) {
        allEnvVars.DATAOPS_CATALOG_OPPORTUNITY_ID = useCaseId;
      }
      if (customerLogoUrl) {
        allEnvVars.DATAOPS_CATALOG_CUSTOMER_LOGO_URL = customerLogoUrl;
      }

      const requestBody: CreateSolutionDeploymentRequest = {
        snowflake_account_id: parseInt(selectedAccount.id),
        project_id: parseInt(solution.id),
        git_ref: gitRef,
        pipeline_filename: pipelineFilename,
        solution_prefix: solutionPrefix,
        extra_env_vars: allEnvVars,
      };

      const response = await axios.post(`${dataopsAdminAppApiEndpoint}/solution_demos/deployments/`, requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Fire checkout-end event on successful deployment creation
      sendEvent({
        name: 'deployment-checkout-end',
        properties: {
          projectName: solutionPrefix,
          customerName,
          snowflakeAccount: snowflakeAccounts.find((a) => a.id.toString() === selectedAccount.id)?.account,
          snowflakeUsername: snowflakeAccounts.find((a) => a.id.toString() === selectedAccount.id)?.username,
          salesForceId: useCaseId,
          solutionProjectId: solution.id,
          workflow: 'new',
        },
      });

      // Redirect to deployment details page
      const deploymentId = response.data.deployment_id as number;
      navigate(`/deployments/${deploymentId}`);
    } catch (err: any) {
      console.error('Failed to create deployment:', err);
      const errorResponse = err.response?.data as ISolutionDeploymentErrorResponse | undefined;
      setError(errorResponse?.message ?? 'Failed to create deployment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canConsume) {
    return (
      <ImportColumnLayout>
        <div className="text-center">
          <h1 className="text-xl">Access Denied</h1>
          <p className="mt-4 text-gray-600">You do not have permission to deploy solutions.</p>
        </div>
      </ImportColumnLayout>
    );
  }

  return (
    <ImportColumnLayout>
      <div>
        <span
          className={classNames(
            'text-dataops-primary-light-blue',
            'inline-flex rounded-lg p-3 ring-1 ring-dataops-primary-light-blue',
          )}
        >
          <RocketLaunchIcon aria-hidden="true" className="h-6 w-6" />
        </span>
      </div>
      <h1 className="text-xl">Deploy Solution</h1>
      <div className="flex space-x-1">
        <div className="inline-flex items-center px-1 space-x-1 text-sm text-gray-800 rounded-md ring-1 ring-inset cursor-default ring-gray-500/10">
          <BeakerIcon className="w-4 h-4" /> <span>New Feature</span>
        </div>
      </div>

      {solution && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-dataops-primary-dark-blue">{solution.listing?.name}</h3>
          <p className="text-sm text-gray-600 mt-1">{solution.listing?.description}</p>
        </div>
      )}

      {solution?.listing?.pre_instance_setup_message && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="font-semibold text-yellow-900 mb-2">Prerequisites</h3>
          <div
            className="text-sm text-yellow-800"
            dangerouslySetInnerHTML={{ __html: solution.listing.pre_instance_setup_message }}
          />
        </div>
      )}

      <div className="space-y-10">
        <SolutionNewSection
          title="Customer Information"
          description={`This information will be used to build out your custom deployment for a customer. You can also keep the customer name generic to re-use the instance for multiple customers.`}
        >
          <Input
            id="customerNameInput"
            label="Customer Name"
            value={customerName}
            error={customerNameError}
            onChange={(e) => {
              const value = e.target.value;
              setCustomerName(value);

              // Validate customer name
              if (value && !isCustomerNameValid(value)) {
                setCustomerNameError(
                  "The customer name can only contain letters, digits, '_', '.', dash, space. It must start with a letter or a digit.",
                );
              } else {
                setCustomerNameError('');
              }

              // Auto-populate solution prefix
              if (value && solution?.listing?.name) {
                const generatedPrefix = getSolutionTitle(
                  value,
                  solution.listing.name,
                  solution.listing.customer_name_suffix,
                );
                setSolutionPrefix(generatedPrefix);
                validatePrefix(generatedPrefix);
              }
            }}
            help={<p>The name of the customer or organization for this deployment.</p>}
          />

          <div>
            <Input
              id="useCaseIdInput"
              label="Use Case ID"
              optional
              value={useCaseId}
              error={useCaseIdError}
              labelExtra={
                <a
                  href="https://snowforce.lightning.force.com/lightning/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm hover:underline text-sky-600 ml-2 flex items-center"
                >
                  Launch Salesforce
                  <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />
                </a>
              }
              onChange={(e) => {
                const value = e.target.value;
                setUseCaseId(value);
                setUseCaseIdError(salesForceIdValidation(value, ValidationLevel.AllowEmpty));
              }}
              help={<p>Optional identifier for tracking the use case or opportunity (e.g., Salesforce ID).</p>}
            />
          </div>

          <div>
            <Input
              id="customerLogoUrlInput"
              label="Customer Logo URL"
              optional
              value={customerLogoUrl}
              onChange={(e) => {
                setCustomerLogoUrl(e.target.value);
                setCustomerLogoUrlError(false);
              }}
              help={<p>Optional URL to the customer&apos;s logo image.</p>}
            />
            <div className="mt-2">
              {customerLogoUrlError || !customerLogoUrl ? (
                <div className="flex items-center">
                  <div className="h-10 w-10 mr-2 border rounded-md border-gray-300"></div>
                  <div className="text-xs">Preview</div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center">
                  <img
                    src={customerLogoUrl}
                    onError={() => {
                      setCustomerLogoUrlError(true);
                    }}
                    alt="Customer logo preview"
                    className="h-10 max-w-full mr-2 border rounded-md border-gray-300"
                  />
                  <div className="text-xs text-lime-600">Preview - Great work!</div>
                </div>
              )}
            </div>
          </div>
        </SolutionNewSection>

        <SolutionNewSection
          title="Deployment Configuration"
          description="Configure the deployment settings and select the Snowflake account where you want to deploy this solution."
        >
          <Input
            id="solutionPrefixInput"
            data-testid="solution-prefix-input"
            label="Solution Prefix"
            icon={HashtagIcon}
            value={solutionPrefix.toUpperCase()}
            placeholder={solutionPrefix.toUpperCase() || 'CUSTOMER_SOLUTION'}
            onChange={handlePrefixChange}
            error={prefixError}
            help={
              <p>Optionally customize the solution prefix. The prefix will be used when naming Snowflake objects.</p>
            }
          />

          <div className="">
            <InputSelect
              id="snowflakeAccountSelect"
              label="Snowflake Account"
              options={accountOptions}
              value={selectedAccount}
              onChange={setSelectedAccount}
              searchable={true}
              error={accountConnectionError}
              help={
                <div className="flex items-start space-x-2">
                  <p>Select the Snowflake account where you want to deploy this solution.</p>
                  {accountConnectionStatus === 'checking' && (
                    <span className="text-sm text-blue-600">Checking connection...</span>
                  )}
                  {accountConnectionStatus === 'valid' && (
                    <span className="text-sm text-green-600">✓ Connection verified</span>
                  )}
                  {accountConnectionStatus === 'invalid' && (
                    <span className="text-sm text-red-600">✗ Connection failed</span>
                  )}
                </div>
              }
            />
            <div className="mt-1 flex justify-end space-x-1">
              <Button
                intent="primary"
                onClick={() => {
                  setIsAddAccountDialogOpen(true);
                }}
              >
                <PlusIcon className="h-4 w-4 mr-1 inline" />
                Add New
              </Button>
              <Button intent="light" onClick={handleManageAccountsClick}>
                Manage
              </Button>
            </div>
          </div>

          {snowflakeAccounts.length === 0 && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                No Snowflake accounts found.{' '}
                <a href="/manage/accounts" className="underline font-semibold">
                  Add a Snowflake account
                </a>{' '}
                to continue.
              </p>
            </div>
          )}
        </SolutionNewSection>

        {canPublish && (
          <div className="space-y-4">
            <div>
              <button
                type="button"
                onClick={() => {
                  setIsAdvancedOptionsExpanded(!isAdvancedOptionsExpanded);
                }}
                className="flex items-center w-full text-left group"
              >
                <h2 className="text-lg font-semibold text-dataops-primary-dark-blue">Advanced Options</h2>
                {isAdvancedOptionsExpanded ? (
                  <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-500 group-hover:text-dataops-primary-dark-blue" />
                ) : (
                  <ChevronRightIcon className="ml-2 h-5 w-5 text-gray-500 group-hover:text-dataops-primary-dark-blue" />
                )}
              </button>
              {!isAdvancedOptionsExpanded && (
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Configure advanced deployment settings such as git reference, pipeline file, and custom environment
                  variables.
                </p>
              )}
            </div>
            {isAdvancedOptionsExpanded && (
              <div className="space-y-4">
                <Input
                  id="gitRefInput"
                  label="Git Reference"
                  value={gitRef}
                  onChange={(e) => {
                    setGitRef(e.target.value);
                  }}
                  help={<p>The git branch, tag, or commit to deploy from. Default is &apos;main&apos;.</p>}
                />

                <Input
                  id="pipelineFilenameInput"
                  label="Pipeline Filename"
                  value={pipelineFilename}
                  onChange={(e) => {
                    setPipelineFilename(e.target.value);
                  }}
                  help={<p>The CI/CD pipeline file to use. Default is &apos;full-ci.yml&apos;.</p>}
                />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Extra Environment Variables (Optional)
                  </label>
                  <InformationTooltip tooltip="Add custom environment variables that will be made available to the pipeline performing the deployment." />
                  <div className="space-y-2">
                    {Object.entries(extraEnvVars).map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                        <span className="font-mono text-sm flex-1">
                          {key} = {value}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            handleRemoveEnvVar(key);
                          }}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Input
                      id="envVarKeyInput"
                      placeholder="KEY"
                      value={envVarKey}
                      onChange={(e) => {
                        setEnvVarKey(e.target.value);
                      }}
                    />
                    <Input
                      id="envVarValueInput"
                      placeholder="value"
                      value={envVarValue}
                      onChange={(e) => {
                        setEnvVarValue(e.target.value);
                      }}
                    />
                    <Button intent="secondary" onClick={handleAddEnvVar} disabled={!envVarKey || !envVarValue}>
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            intent="light"
            size="large"
            onClick={() => {
              navigate(-1);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button intent="primary" size="large" onClick={handleSubmit} disabled={isSubmitting || !solution}>
            {isSubmitting ? 'Deploying...' : 'Deploy Solution'}
          </Button>
        </div>
      </div>

      <AddAccountDialog
        isOpen={isAddAccountDialogOpen}
        onClose={() => {
          setIsAddAccountDialogOpen(false);
        }}
        onSuccess={handleAddAccountSuccess}
      />
    </ImportColumnLayout>
  );
}
